import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { User as UserIcon, ArrowRight, Menu, X, Globe, ChevronDown } from 'lucide-react';

const languages = [
  { code: 'EN', name: 'English', gtCode: 'en' },
  { code: 'SV', name: 'Svenska', gtCode: 'sv' },
  { code: 'ES', name: 'Espanol', gtCode: 'es' },
  { code: 'DE', name: 'Deutsch', gtCode: 'de' },
];

const LANGUAGE_STORAGE_KEY = 'learnweb_selected_language';
const GOOGLE_TRANSLATE_SCRIPT_ID = 'google-translate-element-script';
const GOOGLE_TRANSLATE_SCRIPT_SRC = 'https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';

let googleTranslateLoader;

const getStoredLanguage = () => {
  const storedLanguage = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
  return languages.find((language) => language.code === storedLanguage) || languages[0];
};

const getTranslatedLanguage = () => {
  const googTransCookie = document.cookie
    .split('; ')
    .find((cookie) => cookie.startsWith('googtrans='));
  const translatedCode = googTransCookie?.split('/').pop();

  return languages.find((language) => language.gtCode === translatedCode) || getStoredLanguage();
};

const getCurrentGoogleTranslateCode = () => {
  return document.cookie
    .split('; ')
    .find((cookie) => cookie.startsWith('googtrans='))
    ?.split('/')
    .pop();
};

const setGoogleTranslateCookie = (gtCode) => {
  const value = gtCode === 'en' ? '' : `/en/${gtCode}`;
  const expires = value ? 'Fri, 31 Dec 9999 23:59:59 GMT' : 'Thu, 01 Jan 1970 00:00:00 GMT';
  const hostParts = window.location.hostname.split('.');
  const rootDomain = hostParts.length > 1 ? `.${hostParts.slice(-2).join('.')}` : '';
  const cookie = `googtrans=${value}; expires=${expires}; path=/`;

  document.cookie = cookie;

  if (rootDomain) {
    document.cookie = `${cookie}; domain=${rootDomain}`;
  }
};

const appendHeadLink = (rel, href) => {
  if (document.head.querySelector(`link[rel="${rel}"][href="${href}"]`)) {
    return;
  }

  const link = document.createElement('link');
  link.rel = rel;
  link.href = href;
  if (href.startsWith('https://')) {
    link.crossOrigin = 'anonymous';
  }
  document.head.appendChild(link);
};

const loadGoogleTranslate = () => {
  if (window.google?.translate?.TranslateElement) {
    return Promise.resolve();
  }

  if (googleTranslateLoader) {
    return googleTranslateLoader;
  }

  googleTranslateLoader = new Promise((resolve, reject) => {
    window.googleTranslateElementInit = () => {
      if (!window.google?.translate?.TranslateElement) {
        googleTranslateLoader = null;
        reject(new Error('Google Translate failed to initialize.'));
        return;
      }

      new window.google.translate.TranslateElement(
        {
          pageLanguage: 'en',
          includedLanguages: languages.map((language) => language.gtCode).join(','),
          autoDisplay: false,
        },
        'google_translate_element'
      );

      resolve();
    };

    appendHeadLink('dns-prefetch', 'https://translate.google.com');
    appendHeadLink('dns-prefetch', 'https://translate-pa.googleapis.com');
    appendHeadLink('preconnect', 'https://translate.google.com');
    appendHeadLink('preconnect', 'https://translate-pa.googleapis.com');

    if (document.getElementById(GOOGLE_TRANSLATE_SCRIPT_ID)) {
      return;
    }

    const script = document.createElement('script');
    script.id = GOOGLE_TRANSLATE_SCRIPT_ID;
    script.src = GOOGLE_TRANSLATE_SCRIPT_SRC;
    script.async = true;
    script.onerror = () => {
      googleTranslateLoader = null;
      reject(new Error('Google Translate script failed to load.'));
    };
    document.body.appendChild(script);
  });

  return googleTranslateLoader;
};

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isLangOpen, setIsLangOpen] = useState(false);
  const [selectedLang, setSelectedLang] = useState(() => getTranslatedLanguage().code);
  const [isLoggedIn, setIsLoggedIn] = useState(() => Boolean(localStorage.getItem('authToken')));
  const [hasPaidAccess, setHasPaidAccess] = useState(() => localStorage.getItem('paymentSubmitted') === 'true');
  const { pathname } = useLocation();
  const dropdownRef = useRef(null);
  const translateRetryRef = useRef(null);
  const libraryTarget = isLoggedIn
    ? '/library'
    : '/pricing';
  const shouldShowPricing = !isLoggedIn || !hasPaidAccess;

  useEffect(() => {
    setIsLoggedIn(Boolean(localStorage.getItem('authToken')));
    setHasPaidAccess(localStorage.getItem('paymentSubmitted') === 'true');
  }, [pathname]);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [mobileOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsLangOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const syncSelectedLanguage = () => {
      const translatedLanguage = getTranslatedLanguage();
      setSelectedLang(translatedLanguage.code);
      window.localStorage.setItem(LANGUAGE_STORAGE_KEY, translatedLanguage.code);
    };

    syncSelectedLanguage();
    const intervalId = window.setInterval(syncSelectedLanguage, 1000);

    return () => window.clearInterval(intervalId);
  }, []);

  const changeLanguage = (gtCode, attempt = 0) => {
    const select = document.querySelector('.goog-te-combo');
    const translatedCode = getCurrentGoogleTranslateCode() || 'en';

    if (translatedCode === gtCode && attempt === 0) {
      return;
    }

    setGoogleTranslateCookie(gtCode);

    if (translateRetryRef.current) {
      window.clearTimeout(translateRetryRef.current);
      translateRetryRef.current = null;
    }

    if (!select) {
      loadGoogleTranslate();

      if (attempt < 10) {
        translateRetryRef.current = window.setTimeout(() => changeLanguage(gtCode, attempt + 1), 250);
      }
      return;
    }

    select.value = gtCode === 'en' ? '' : gtCode;
    select.dispatchEvent(new Event('change', { bubbles: true }));

    if ((getCurrentGoogleTranslateCode() || 'en') !== gtCode && attempt < 10) {
      translateRetryRef.current = window.setTimeout(() => changeLanguage(gtCode, attempt + 1), 250);
    }
  };

  const handleLangChange = async (lang) => {
    setSelectedLang(lang.code);
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, lang.code);

    try {
      await loadGoogleTranslate();
    } catch (error) {
      return;
    }

    changeLanguage(lang.gtCode);
    setIsLangOpen(false);
  };

  // Premium animated underline for desktop links
  const navLink = (to, label, target = to) => {
    const isActive = pathname === to;
    return (
      <Link
        to={target}
        className={`relative group px-1 py-2 text-sm font-medium transition-colors duration-300 ${
          isActive ? 'text-white' : 'text-slate-300 hover:text-white'
        }`}
      >
        {label}
        {/* Animated Underline */}
        <span 
          className="absolute bottom-0 left-0 h-[2px] w-0 bg-gradient-to-r from-[#2563EB] to-[#EC4899] transition-all duration-300 ease-out group-hover:w-full" 
        />
      </Link>
    );
  };

  const mobileNavLink = (to, label, target = to) => (
    <Link
      to={target}
      onClick={() => setMobileOpen(false)}
      className={`block px-4 py-3 rounded-lg text-base font-medium transition-all duration-300 ${
        pathname === to
          ? 'bg-slate-800 text-white'
          : 'text-slate-400 hover:text-white hover:bg-slate-800/50'
      }`}
    >
      {label}
    </Link>
  );

  return (
    <>
      {/* FULL WIDTH WRAPPER */}
      <header className="fixed top-0 left-0 right-0 z-50 w-full bg-[#0F172A]/85 backdrop-blur-xl border-b border-slate-800/80 transition-all duration-300">
        
        {/* Ultra-thin gradient accent line at the very top to match your hero text */}
        <div className="absolute top-20 left-0 right-0 h-[2px] bg-gradient-to-r from-[#2563EB] via-purple-500 to-[#EC4899] opacity-80" />

        {/* Inner Container (Max-width for ultra-wide monitors, but feels full width) */}
        <div className="max-w-[1360px] mx-auto px-2 sm:px-6 lg:px-2">
    <nav className="flex items-center justify-between h-20 w-full">
            
            {/* LEFT: LOGO */}
            <div className="flex-shrink-0 flex items-center">
              <Link to="/" className="group">
                <img 
                  src="/personalfinanceacademy-logo.svg?v=2" 
                  alt="Logo" 
                  width={460}
                  height={67}
                  decoding="async"
                  className="w-[240px] sm:w-[260px] md:w-[320px] transition-opacity duration-300 group-hover:opacity-80" 
                />
              </Link>
            </div>          

            {/* CENTER: NAVIGATION (Single Row) */}
            <div className="hidden lg:flex items-center justify-end gap-10 flex-1 px-8">
              {navLink('/', 'Home')}
              {navLink('/library', 'Library', libraryTarget)}
              {shouldShowPricing && navLink('/pricing', 'Pricing')}
              {navLink('/unsubscribe', 'Unsubscribe')}
            </div> {/* <-- ADDED CLOSING DIV HERE */}

            {/* RIGHT: ACTIONS */}
            <div className="flex items-center gap-4 sm:gap-6 flex-shrink-0">
              <div ref={dropdownRef} className="relative notranslate" translate="no">
                <button
                  type="button"
                  onClick={() => {
                    const nextState = !isLangOpen;
                    setIsLangOpen(nextState);

                    if (nextState) {
                      void loadGoogleTranslate();
                    }
                  }}
                  className="flex items-center gap-1.5 text-sm font-bold text-slate-200 hover:text-white transition-colors duration-300"
                  translate="no"
                  aria-label="Select language"
                  aria-expanded={isLangOpen}
                >
                  <Globe className="w-4 h-4 text-[#38BDF8]" />
                  <span className="min-w-5 text-left">{selectedLang}</span>
                  <ChevronDown
                    className={`w-3.5 h-3.5 transition-transform duration-300 ${isLangOpen ? 'rotate-180' : ''}`}
                  />
                </button>

                {isLangOpen && (
                  <div className="absolute right-0 top-full mt-5 w-36 overflow-hidden rounded-2xl border border-slate-200 bg-white py-2 shadow-2xl shadow-black/20">
                    {languages.map((lang) => (
                      <button
                        key={lang.code}
                        type="button"
                        onClick={() => handleLangChange(lang)}
                        translate="no"
                        className={`w-full px-5 py-2.5 text-left text-sm font-bold transition-colors hover:bg-[#4CE470]/10 ${
                          selectedLang === lang.code
                            ? 'bg-slate-50 text-[#113A28]'
                            : 'text-slate-500'
                        }`}
                      >
                        {lang.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              
              {isLoggedIn && (
                <Link 
                  to="/my-account" 
                  className="hidden sm:flex items-center gap-2 text-slate-300 hover:text-white transition-colors duration-300 group"
                >
                  <div className="w-9 h-9 rounded-full bg-slate-800 flex items-center justify-center border border-slate-700 group-hover:border-[#2563EB] transition-colors">
                    <UserIcon className="w-4 h-4" />
                  </div>
                </Link>
              )}

              {/* Gradient Border Button */}
              {!isLoggedIn && (
                <Link 
                  to="/signup" 
                  className="hidden md:flex items-center gap-2 relative group"
                >
                  <div className="absolute -inset-0.5 bg-gradient-to-r from-[#2563EB] to-[#EC4899] rounded-lg blur opacity-40 group-hover:opacity-75 transition duration-300"></div>
                  <div className="relative flex items-center gap-2 bg-[#0F172A] border border-slate-700/50 text-white px-6 py-2.5 rounded-lg text-sm font-semibold hover:bg-slate-900 transition-colors">
                    Sign up
                    <ArrowRight className="w-4 h-4" />
                  </div>
                </Link>
              )}

              {/* Mobile Menu Toggle */}
              <button
                onClick={() => setMobileOpen(true)}
                className="lg:hidden p-2 -mr-2 text-slate-300 hover:text-white transition-colors"
              > 
                <Menu className="w-6 h-6" />
              </button>
            </div>

          </nav>
        </div>
      </header>

      {/* Spacer to prevent content from hiding under the fixed full-width header */}
      <div className="h-20 w-full" />
      <div id="google_translate_element" className="google-translate-shell" />

      {/* MOBILE NAV (Standard Slide-Down) */}
      <div 
        className={`fixed inset-0 z-[999] lg:hidden transition-opacity duration-300 ${
          mobileOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
      >
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
        
        <div 
          className={`absolute top-0 left-0 right-0 bg-[#0F172A] border-b border-slate-800 p-4 transition-transform duration-300 ease-out ${
            mobileOpen ? 'translate-y-0' : '-translate-y-full'
          }`}
        >
          <div className="flex items-center justify-between mb-6 px-2">
            <img src="/personalfinanceacademy-logo.svg?v=2" alt="Logo" width={460} height={67} decoding="async" className="w-[230px]" />
            <button onClick={() => setMobileOpen(false)} className="p-2 text-slate-400 hover:text-white">
              <X className="w-6 h-6" />
            </button>
          </div>
          
          <div className="flex flex-col gap-2">
            {mobileNavLink('/', 'Home')}
            {mobileNavLink('/library', 'Library', libraryTarget)}
            {shouldShowPricing && mobileNavLink('/pricing', 'Pricing')}
            {mobileNavLink('/unsubscribe', 'Unsubscribe')}
            {isLoggedIn && mobileNavLink('/my-account', 'Account')}
            
            {!isLoggedIn && <div className="mt-4 pt-4 border-t border-slate-800">
              <Link 
                to="/signup" 
                onClick={() => setMobileOpen(false)} 
                className="flex items-center justify-center gap-2 w-full bg-gradient-to-r from-[#2563EB] to-[#EC4899] text-white px-6 py-3 rounded-lg text-base font-semibold"
              >
                Sign up <ArrowRight className="w-5 h-5" />
              </Link>
            </div>}
          </div>
        </div>
      </div>
    </>
  );
}
